import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';
export type Density = 'comfortable' | 'compact' | 'dense';

const DENSITY_CYCLE: Density[] = ['comfortable', 'compact', 'dense'];

interface UiState {
  /** User theme preference; 'system' follows the OS. */
  themeMode: ThemeMode;
  /** Data-density preference applied app-wide (grids honor it). */
  density: Density;
  /** Desktop sidebar collapsed to icon rail. */
  sidebarCollapsed: boolean;
  /**
   * Menu groups the user opened (true) or closed (false), by group name. A
   * group without a choice is open when it is the person's own (see
   * homeGroups) or holds the current route; navigating never stores a choice.
   */
  navGroupsOpen: Record<string, boolean>;

  // Transient (not persisted)
  paletteOpen: boolean;
  shortcutsOpen: boolean;

  setThemeMode: (mode: ThemeMode) => void;
  setDensity: (density: Density) => void;
  cycleDensity: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setNavGroupOpen: (group: string, open: boolean) => void;
  setPaletteOpen: (open: boolean) => void;
  setShortcutsOpen: (open: boolean) => void;
}

/** The subset actually written to localStorage — see `partialize` below. */
type PersistedUi = Pick<UiState, 'themeMode' | 'density' | 'sidebarCollapsed' | 'navGroupsOpen'>;

/** Legacy key used by MainLayout before the ui store existed. */
function legacySidebarCollapsed(): boolean {
  try {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  } catch {
    return false;
  }
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      themeMode: 'light',
      density: 'compact',
      sidebarCollapsed: legacySidebarCollapsed(),
      navGroupsOpen: {},

      paletteOpen: false,
      shortcutsOpen: false,

      setThemeMode: (themeMode) => set({ themeMode }),
      setDensity: (density) => set({ density }),
      cycleDensity: () =>
        set((s) => ({
          density: DENSITY_CYCLE[(DENSITY_CYCLE.indexOf(s.density) + 1) % DENSITY_CYCLE.length],
        })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      toggleSidebarCollapsed: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

      setNavGroupOpen: (group, open) => set((s) => ({ navGroupsOpen: { ...s.navGroupsOpen, [group]: open } })),

      setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
      setShortcutsOpen: (shortcutsOpen) => set({ shortcutsOpen }),
    }),
    {
      name: 'ui-preferences',
      // v2 dropped pinnedReports / recentReports; v3 replaced the Reports
      // sub-sections (expandedNavSections) with whole menu groups. Bumping the
      // version runs the migration below, so an existing user's stored blob is
      // rewritten without those keys instead of carrying dead state forever.
      version: 3,
      migrate: (persisted): PersistedUi => {
        const old = (persisted ?? {}) as Partial<PersistedUi>;
        // Only the keys that still exist are carried over; anything the old
        // blob held (pinnedReports, recentReports, expandedNavSections) is
        // simply not read.
        return {
          themeMode: old.themeMode ?? 'light',
          density: old.density ?? 'compact',
          sidebarCollapsed: old.sidebarCollapsed ?? legacySidebarCollapsed(),
          navGroupsOpen: old.navGroupsOpen ?? {},
        };
      },
      partialize: (s) => ({
        themeMode: s.themeMode,
        density: s.density,
        sidebarCollapsed: s.sidebarCollapsed,
        navGroupsOpen: s.navGroupsOpen,
      }),
    },
  ),
);

/** Resolve the theme actually applied, honoring the OS for 'system'. */
export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return mode;
}
